import LoadingYourSongbookIndicator from "@/components/LoadingYourSongbookIndicator";
import { useAppStore, useHandleClickCreateNewSong } from "@/store/app";
import type { Song } from "@/utils/types";
import { startCase, toLower } from "lodash";
import { useEffect, useState } from "react";
import { MdAddCircle, MdOutlineManageSearch } from "react-icons/md";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";

export default function SearchFooter() {
  const searchKey = useAppStore(({ searchKey }) => searchKey);
  const handleClickCreateNewSong = useHandleClickCreateNewSong();

  return (
    <>
      <SearchResultsList />
      <div className="border-t-[1px] border-accent-content">
        <div
          className="flex px-4 hover:bg-[#ffffff10] py-3 cursor-pointer items-center text-accent-content"
          onClick={() => {
            window.analyticsLogEvent("Clicked Search Web in SearchFooter", {
              searchKey, // TODO: possibly pii
            });
            window.open(
              `https://google.com/search?q=${searchKey} lyrics and chords`,
              "_blank",
            );
          }}
        >
          <MdOutlineManageSearch className="text-2xl mr-6" />
          <span>{`Search Web “${searchKey}”`}</span>
        </div>
        <div
          className="flex px-4 hover:bg-[#ffffff10] py-3 cursor-pointer items-center text-accent-content"
          onClick={(e) => {
            window.analyticsLogEvent("Clicked Add Song in SearchFooter");
            handleClickCreateNewSong(e);
          }}
        >
          <MdAddCircle className="text-2xl mr-6" />
          <span>Add Song</span>
        </div>
      </div>
    </>
  );
}
// import { Effect } from "effect";

// const getSearchResults = (q: string): Effect.Effect<unknown, HttpClientError> =>
//   Http.request.get(`/api/search?q=${q}`).pipe(
//     Http.client.fetchOk(),
//     Http.response.json,
//   );

const SearchResultsList = () => {
  const searchKey = useAppStore(({ searchKey }) => searchKey);
  const [searchKeyDebounced] = useDebounce(searchKey, 500);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.debug("SearchResults loading", { searchKeyDebounced });
    if (!searchKeyDebounced) return;
    setLoading(true);
    const fn = async () => {
      const response = await fetch(`/api/search?q=${searchKeyDebounced}`);
      const results = await response.json();
      console.debug("SearchResults", { results });
      setResults(results);
      setLoading(false);
    };
    fn();
  }, [searchKeyDebounced]);

  if (loading)
    return (
      <div className="border-t-[1px] border-accent-content py-2 px-3">
        <div className="loading-spinner">
          <div className="spinner">
            <div />
          </div>
        </div>
      </div>
    );

  return (
    <div className="border-t-[1px] border-accent-content">
      {results.map((result: SearchResult) => (
        <SearchResultListItem key={result.cacheId} result={result} />
      ))}
    </div>
  );
};

interface SearchResult {
  cacheId: string;
  link: string;
  title: string;
}

interface SearchResultListItemProps {
  result: SearchResult;
}

const SearchResultListItem = ({ result }: SearchResultListItemProps) => {
  const { setSearchKey, setIsSearchOpen } = useAppStore(
    ({ setSearchKey, setIsSearchOpen }) => ({ setSearchKey, setIsSearchOpen }),
    shallow,
  );
  const handleClickCreateNewSong = useHandleClickCreateNewSong();
  const url = encodeURI(result.link);
  const [loading, setLoading] = useState(false);

  const handleClick = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/rescue?url=${url}`);
      const json = await response.json();
      console.debug("SearchResultListItem RESCUE", { url, json });
      const song = {
        id: "",
        title: json.title,
        artist: json.artist,
        capo: 0,
        chordPro: json.chordPro,
      } as Song;
      await handleClickCreateNewSong(e, song);
      setIsSearchOpen(false);
      setSearchKey("");
    } catch (err) {
      console.error("SearchResultListItem RESCUE fail", { err });
    }
    setLoading(false);
  };

  const resultTitle = startCase(
    toLower(result.title.replace(/ \@ Ultimate.+/, "")),
  );

  return (
    <>
      {loading && <LoadingYourSongbookIndicator />}
      <div
        className="flex items-center cursor-pointer border-l-4 py-3 pr-4 pl-3 text-accent-content border-transparent hover:border-base-content"
        onClick={handleClick}
      >
        {resultTitle}
      </div>
    </>
  );
};
